import { Component, inject, Input } from '@angular/core';
import { FilterService } from '../../../../../_shared/services';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-cost-per-kilometer-widget',
  standalone: true,
  imports: [
    KpiWidgetComponent,
    TranslocoPipe
],
  templateUrl: './cost-per-kilometer-widget.component.html',
  styleUrl: './cost-per-kilometer-widget.component.scss'
})
export class CostPerKilometerWidgetComponent extends BaseWidgetComponent {
  private readonly transloco = inject(TranslocoService);
  @Input() selectedFilter: string;

  private executiveStatsService = inject(ExecutiveStatsService);
  private auth = inject(AuthService);
  private errorHandlerService = inject(ErrorHandlerService);

  hasError: boolean = false;
  errorMessage: string = '';
  currentDate: Date = new Date();
  cost: number = 0;

  selectedUnit: string = MeasurementTypes.CostPerUnit.toString();
  dropdownOptions: DropdownOption[] = []

  currencySymbol: string = '';
  currencyCode: string = '';

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.percentageDifference = 0;
    this.difference = 0;

    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.dropdownOptions = [
      { label: `${this.currencySymbol}/Km`, value: MeasurementTypes.CostPerUnit.toString(), unit: '/km' }
    ];
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      const request: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      }

      this.cancelSubject.next();
      const response = await this.executiveStatsService.getCostPerKilometer(request, this.cancelSubject);
      this.value = response.data?.totalValue ?? 0;
      this.cost = response.data?.cost ?? 0;
      this.percentageDifference = response.data?.percentageDifference ?? 0;
      this.difference = response.data?.difference ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }
}